<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  unit: {
    type: Object as PropType<any>,
    required: true,
  },
});

const scale = ref(1);
const panX = ref(0);
const panY = ref(0);
const isDragging = ref(false);
const startX = ref(0);
const startY = ref(0);
const isFullscreen = ref(false);

const onKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isFullscreen.value) {
    e.preventDefault();
    e.stopPropagation();
    toggleFullscreen();
  }
};

onMounted(() => {
  document.addEventListener('keydown', onKeyDown, true);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown, true);
});

const zoomIn = () => {
  scale.value = Math.min(scale.value + 0.25, 5);
};

const zoomOut = () => {
  scale.value = Math.max(scale.value - 0.25, 0.5);
};

const resetZoom = () => {
  scale.value = 1;
  panX.value = 0;
  panY.value = 0;
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
  resetZoom();
};

const onMouseDown = (e: MouseEvent) => {
  isDragging.value = true;
  startX.value = e.clientX - panX.value;
  startY.value = e.clientY - panY.value;
};

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) return;
  panX.value = e.clientX - startX.value;
  panY.value = e.clientY - startY.value;
};

const onMouseUp = () => {
  isDragging.value = false;
};

const onWheel = (e: WheelEvent) => {
  if (e.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
};

const transformStyle = computed(() => {
  return {
    transform: `translate(${panX.value}px, ${panY.value}px) scale(${scale.value})`,
    cursor: isDragging.value ? 'grabbing' : 'grab',
    transition: isDragging.value ? 'none' : 'transform 0.1s ease',
    transformOrigin: 'center center'
  };
});
</script>

<template>
  <div 
    class="p-4 flex justify-center items-center"
    :class="isFullscreen ? 'fixed inset-0 z-[100] bg-black/90' : 'h-full'"
  >
    <div 
      class="relative flex flex-col rounded-lg shadow-lg border overflow-hidden bg-white group"
      :class="isFullscreen ? 'w-full h-full border-none rounded-none bg-transparent' : 'w-fit'"
    >
      <!-- Toolbar -->
      <div class="absolute top-0 right-0 z-10 flex gap-2 bg-black/60 backdrop-blur rounded-bl-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <button @click="zoomIn" class="p-2 hover:bg-black/40 rounded text-white transition-colors" title="Zoom In">
          <icon name="lucide:zoom-in" size="20" />
        </button>
        <button @click="zoomOut" class="p-2 hover:bg-black/40 rounded text-white transition-colors" title="Zoom Out">
          <icon name="lucide:zoom-out" size="20" />
        </button>
        <button @click="toggleFullscreen" class="p-2 hover:bg-black/40 rounded text-white transition-colors" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
          <icon :name="isFullscreen ? 'lucide:shrink' : 'lucide:expand'" size="20" />
        </button>
      </div>

      <!-- Image Container -->
      <div 
        class="overflow-hidden relative flex-1 flex items-center justify-center"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
        @wheel.prevent="onWheel"
      >
        <img 
          :src="unit.imagePath" 
          class="object-contain block select-none pointer-events-none"
          :class="isFullscreen ? 'w-full h-full' : 'h-[70vh] w-auto max-w-[80vw]'"
          :style="transformStyle"
          draggable="false"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
